import isEqual from 'lodash/isEqual'
import { useState, useCallback } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { format } from 'date-fns' // eslint-disable-line
import jsPDF from 'jspdf'
// @mui
import Card from '@mui/material/Card'
import Container from '@mui/material/Container'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import { Box, Typography, Stack, Chip } from '@mui/material'

// components
import { paths } from 'src/routes/paths'
import { useSettingsContext } from 'src/components/settings'
import Iconify from 'src/components/iconify'

import Datagrid from 'src/components/datagrid'

//
import { Helmet } from 'react-helmet-async'

import { IReportTableFilterValue, IReportTableFilters } from 'src/types/reports'
import useReports from 'src/service/reports.service'
import ProductTableRow from './table-user/reports-table-row'
import ReportTableFiltersResult from './table-user/reports-table-filters-result'
import ReportTableToolbar from './table-user/reports-table-toolbar'

const fmtBRL = (v: number) => {
  if (!v) return 'R$ 0,00'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v / 100)
}

function loadLogoAsDataUrl(): Promise<string | null> {
  return new Promise((resolve) => {
    // eslint-disable-next-line
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(null)
        return
      }
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => resolve(null)
    img.src = '/logo/logo.svg'
  })
}

const TABLE_HEAD = [
  { id: 'name', label: 'Nome do Produto', width: 200 },
  { id: 'category', label: 'Categoria', width: 140 },
  { id: 'stock', label: 'Estoque Disponível', width: 100 },
  { id: 'saida', label: 'Quantidade Vendida', width: 100 },
  { id: 'price', label: 'Preço Unitário', width: 100 },
  { id: 'totalSale', label: 'Faturamento Total', width: 120 },
  { id: 'cost', label: 'Custo por Unidade', width: 100 },
  { id: 'stockCost', label: 'Valor do Estoque', width: 120 },
]

const defaultFilters: IReportTableFilters = {
  event: null,
  dateStart: null,
  dateEnd: null,
  salers: [],
  categories: [],
  terminalSerial: '',
  includeInactive: false,
}

export default function ReportsListView() {
  const [filters, setFilters] = useState<IReportTableFilters>(defaultFilters)

  // Paginação com valor alto para mostrar todos os itens
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 1000,
    sort: {
      createdAt: 'desc',
    },
  })

  const { isLoading, isFetching, data, refetch } = useReports(pagination, filters)

  const settings = useSettingsContext()

  const canReset = !isEqual(defaultFilters, filters)

  const notFound = (!data?.length && canReset) || !data?.length

  const inactiveCount = Array.isArray(data)
    ? data.filter((p: any) => p?.active === false).length
    : 0

  const handleFilters = useCallback((name: string, value: IReportTableFilterValue) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }, [])

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters)
  }, [])

  // Função para exportar para Excel
  const handleExportExcel = useCallback(() => {
    if (!data || data.length === 0) {
      alert('Não há dados para exportar')
      return
    }

    // Preparar dados para exportação
    const exportData = data.map((item: any) => ({
      'Nome do Produto': item.name || '',
      Categoria: item?.category?.name || '-',
      Status: item.active === false ? 'Desativado' : 'Ativo',
      'Estoque Disponível': item.stock || 0,
      'Quantidade Vendida': item.saida || 0,
      'Preço Unitário': `R$ ${((item.price || 0) / 100).toFixed(2).replace('.', ',')}`,
      'Faturamento Total': `R$ ${((item.totalSale || 0) / 100).toFixed(2).replace('.', ',')}`,
      'Custo por Unidade': `R$ ${((item.cost || 0) / 100).toFixed(2).replace('.', ',')}`,
      'Valor do Estoque': `R$ ${((item.stockCost || 0) / 100).toFixed(2).replace('.', ',')}`,
    }))

    // Criar CSV
    const headers = Object.keys(exportData[0])
    const csvContent = [
      headers.join(';'),
      ...exportData.map((row) =>
        headers.map((header) => `"${row[header as keyof typeof row]}"`).join(';'),
      ),
    ].join('\n')

    // Download do arquivo
    const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute(
      'download',
      `relatorio-produtos-${new Date().toISOString().split('T')[0]}.csv`,
    )
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [data])

  const handleExportPDF = useCallback(async () => {
    if (!data || data.length === 0) return

    const logoDataUrl = await loadLogoAsDataUrl()

    // eslint-disable-next-line new-cap
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14
    const contentWidth = pageWidth - margin * 2
    let y = 20

    const headerHeight = 40
    doc.setFillColor(99, 102, 241)
    doc.rect(0, 0, pageWidth, headerHeight, 'F')

    doc.setTextColor(255, 255, 255)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(18)
    doc.text('Relatório de Produtos', margin, 18)

    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    const subtitle = filters.event
      ? `Evento: ${filters.event.label}`
      : 'Todos os eventos'
    doc.text(subtitle, margin, 27)

    const dateStr = format(new Date(), 'dd/MM/yyyy HH:mm')
    doc.text(`Gerado em: ${dateStr}`, margin, 34)
    doc.text(`${data.length} produto(s)`, pageWidth - margin, 34, { align: 'right' })

    y = headerHeight + 12

    doc.setFillColor(245, 247, 250)
    doc.rect(margin, y - 5, contentWidth, 10, 'F')
    doc.setDrawColor(99, 102, 241)
    doc.setLineWidth(0.5)
    doc.line(margin, y + 5, margin + contentWidth, y + 5)

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(7)
    doc.setTextColor(80, 80, 80)

    const colX = {
      name: margin + 2,
      stock: margin + 58,
      sold: margin + 78,
      price: margin + 98,
      revenue: margin + 122,
      cost: margin + 148,
      stockCost: margin + 168,
    }

    doc.text('Produto', colX.name, y)
    doc.text('Estoque', colX.stock, y)
    doc.text('Vendido', colX.sold, y)
    doc.text('Preço Unit.', colX.price, y)
    doc.text('Faturamento', colX.revenue, y)
    doc.text('Custo Unit.', colX.cost, y)
    doc.text('Vlr Estoque', colX.stockCost, y)
    y += 10

    const totals = { stock: 0, sold: 0, revenue: 0, stockCost: 0 }

    data.forEach((item: any, idx: number) => {
      if (y > pageHeight - 30) {
        doc.addPage()
        y = 20
      }

      if (idx % 2 === 0) {
        doc.setFillColor(250, 250, 252)
        doc.rect(margin, y - 5, contentWidth, 9, 'F')
      }

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(7)
      const isInactive = item.active === false

      // Nome do produto - mostra cinza quando desativado e adiciona tag
      if (isInactive) {
        doc.setTextColor(140, 140, 140)
      } else {
        doc.setTextColor(60, 60, 60)
      }
      const baseName = (item.name || 'N/A').substring(0, isInactive ? 24 : 30)
      const productName = isInactive ? `${baseName} [DESATIVADO]` : baseName
      doc.text(productName, colX.name, y)
      const categoryName = item?.category?.name
      if (categoryName) {
        const prevColor = isInactive ? 160 : 130
        doc.setTextColor(prevColor, prevColor, prevColor)
        doc.setFontSize(6)
        doc.text(`Cat.: ${String(categoryName).substring(0, 26)}`, colX.name, y + 3.2)
        doc.setFontSize(7)
        doc.setTextColor(isInactive ? 140 : 60, isInactive ? 140 : 60, isInactive ? 140 : 60)
      }
      doc.text(String(item.stock || 0), colX.stock, y)
      doc.text(String(item.saida || 0), colX.sold, y)
      doc.text(fmtBRL(item.price || 0), colX.price, y)

      doc.setFont('helvetica', 'bold')
      doc.setTextColor(isInactive ? 140 : 46, isInactive ? 140 : 125, isInactive ? 140 : 50)
      doc.text(fmtBRL(item.totalSale || 0), colX.revenue, y)

      doc.setFont('helvetica', 'normal')
      doc.setTextColor(isInactive ? 140 : 60, isInactive ? 140 : 60, isInactive ? 140 : 60)
      doc.text(fmtBRL(item.cost || 0), colX.cost, y)
      doc.text(fmtBRL(item.stockCost || 0), colX.stockCost, y)

      totals.stock += item.stock || 0
      totals.sold += item.saida || 0
      totals.revenue += item.totalSale || 0
      totals.stockCost += item.stockCost || 0

      y += 9
    })

    if (y > pageHeight - 40) {
      doc.addPage()
      y = 20
    }

    y += 4
    doc.setDrawColor(99, 102, 241)
    doc.setLineWidth(0.8)
    doc.line(margin, y - 3, margin + contentWidth, y - 3)

    doc.setFillColor(240, 242, 255)
    doc.rect(margin, y - 2, contentWidth, 10, 'F')
    y += 4

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(7)
    doc.setTextColor(60, 60, 80)
    doc.text('TOTAL', colX.name, y)
    doc.text(String(totals.stock), colX.stock, y)
    doc.text(String(totals.sold), colX.sold, y)
    doc.text('', colX.price, y)

    doc.setTextColor(46, 125, 50)
    doc.text(fmtBRL(totals.revenue), colX.revenue, y)

    doc.setTextColor(60, 60, 80)
    doc.text('', colX.cost, y)
    doc.text(fmtBRL(totals.stockCost), colX.stockCost, y)

    const footerY = pageHeight - 15
    doc.setDrawColor(200, 200, 200)
    doc.setLineWidth(0.3)
    doc.line(margin, footerY, pageWidth - margin, footerY)

    if (logoDataUrl) {
      const logoH = 5
      const logoW = logoH * (3652 / 735)
      doc.addImage(logoDataUrl, 'PNG', margin, footerY + 1.5, logoW, logoH)
    }

    doc.setTextColor(160, 160, 160)
    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    const textX = logoDataUrl ? margin + 30 : margin
    doc.text('DevBrasil Events - Relatório de Produtos', textX, footerY + 6)
    doc.text('Documento gerado automaticamente', pageWidth - margin, footerY + 6, {
      align: 'right',
    })

    const slug = filters.event
      ? (filters.event.label || 'evento').replace(/\s+/g, '-').toLowerCase()
      : 'geral'
    doc.save(`relatorio-produtos-${slug}-${format(new Date(), 'yyyy-MM-dd')}.pdf`)
  }, [data, filters.event])

  const handleRefresh = useCallback(() => {
    refetch()
  }, [refetch])

  return (
    <>
      <Helmet>
        <title>Relatório de produtos - Listagem</title>
      </Helmet>

      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        {/* Header compacto */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            mb: { xs: 2, md: 2.5 },
            color: 'white',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent='space-between'
            spacing={1.5}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(255, 255, 255, 0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon='mdi:chart-box-outline' width={20} />
              </Box>
              <Box>
                <Typography
                  variant='subtitle1'
                  fontWeight={700}
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, lineHeight: 1.2 }}
                >
                  Relatório de Produtos
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    opacity: 0.85,
                    fontSize: '0.75rem',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  Análise completa de estoque e vendas
                </Typography>
              </Box>
            </Stack>

            <Stack direction='row' spacing={0.75} alignItems='center'>
              <Chip
                label={`${data?.length || 0} produtos`}
                size='small'
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.18)',
                  color: 'white',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  '& .MuiChip-label': { px: 1.25 },
                }}
              />
              {inactiveCount > 0 && (
                <Chip
                  label={`${inactiveCount} desativado${inactiveCount === 1 ? '' : 's'}`}
                  size='small'
                  sx={{
                    bgcolor: 'rgba(244, 67, 54, 0.25)',
                    color: 'white',
                    fontWeight: 600,
                    border: '1px solid rgba(244, 67, 54, 0.5)',
                  }}
                />
              )}
              {canReset && (
                <Chip
                  label='Filtros aplicados'
                  size='small'
                  sx={{
                    bgcolor: 'rgba(255, 193, 7, 0.25)',
                    color: 'white',
                    fontWeight: 600,
                    border: '1px solid rgba(255, 193, 7, 0.5)',
                  }}
                />
              )}
            </Stack>
          </Stack>
        </Box>

        {filters.event ? (
          <MoreAnalysisBanner eventId={filters.event.value as string} />
        ) : null}

        <Card
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            gap={1.5}
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Stack direction='row' alignItems='center' gap={1.25}>
              <Iconify icon='solar:filter-bold' width={18} sx={{ color: 'text.secondary' }} />
              <Typography variant='subtitle2' fontWeight={700} color='text.primary'>
                Filtros
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: { xs: 'none', md: 'inline' } }}
              >
                · Selecione evento e período para gerar o relatório
              </Typography>
            </Stack>
            {canReset && (
              <Box
                component='button'
                onClick={handleResetFilters}
                sx={{
                  border: 'none',
                  bgcolor: 'transparent',
                  color: 'error.main',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'error.lighter' },
                }}
              >
                <Iconify icon='solar:close-circle-bold' width={14} />
                Limpar
              </Box>
            )}
          </Stack>

          <ReportTableToolbar
            filters={filters}
            onFilters={handleFilters}
            onExportExcel={handleExportExcel}
            onExportPDF={handleExportPDF}
            onRefresh={handleRefresh}
            hasData={data && data.length > 0}
            isLoading={isLoading || isFetching}
          />

          {canReset && (
            <ReportTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              onResetFilters={handleResetFilters}
              results={data?.length || 0}
              sx={{ p: 2, pt: 0 }}
            />
          )}

          {!filters.event ? (
            <SelectEventPlaceholder />
          ) : (
            <Datagrid
              data={data || []}
              notFound={notFound}
              noSelection
              defaultDense
              isLoading={isLoading || isFetching}
              headLabel={TABLE_HEAD}
              refetch={refetch}
              total={data?.length || 0}
              pagination={pagination}
              setPagination={setPagination}
              ItemContent={ProductTableRow}
            />
          )}
        </Card>
      </Container>
    </>
  )
}

// ---------------------------------------------------------------------------

function SelectEventPlaceholder() {
  return (
    <Box
      sx={{
        p: { xs: 4, md: 6 },
        textAlign: 'center',
        background:
          'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: 'primary.lighter',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
        }}
      >
        <Iconify icon='solar:calendar-search-bold' width={36} color='primary.main' />
      </Box>
      <Typography variant='h6' fontWeight={700} color='text.primary' gutterBottom>
        Selecione um evento
      </Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ maxWidth: 480, mx: 'auto', mb: 1 }}
      >
        Para visualizar o relatório de produtos, utilize o filtro acima e selecione o evento
        desejado.
      </Typography>
      <Typography variant='caption' color='text.disabled'>
        Os dados de estoque, vendas e faturamento serão exibidos após a seleção.
      </Typography>
    </Box>
  )
}

// ---------------------------------------------------------------------------

function MoreAnalysisBanner({ eventId }: { eventId?: string }) {
  const eventQuery = eventId ? `?event=${eventId}` : ''

  const items = [
    {
      title: 'Análise Visual de Produtos',
      description:
        'Gráficos por horário, ranking, mapa de calor e tendências de venda',
      icon: 'solar:chart-2-bold',
      iconColor: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      href: `${paths.dashboard.productsAnalysis}${eventQuery}`,
      cta: 'Ver Análise',
    },
    {
      title: 'Performance de Vendedores',
      description:
        'Ranking de vendedores, ticket médio, métodos de pagamento e especialização',
      icon: 'solar:users-group-two-rounded-bold',
      iconColor: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      href: `${paths.dashboard.sellerPerformance}${eventQuery}`,
      cta: 'Ver Performance',
    },
  ]

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'primary.lighter',
        background:
          'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(168, 85, 247, 0.04) 100%)',
      }}
    >
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1.25 }}>
        <Iconify
          icon='solar:lightbulb-bolt-bold'
          width={18}
          sx={{ color: 'warning.main' }}
        />
        <Typography variant='subtitle2' fontWeight={700} sx={{ fontSize: 13 }}>
          Quer ir além? Veja também:
        </Typography>
        <Chip
          label='Novo'
          size='small'
          color='warning'
          variant='soft'
          sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
        />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
        {items.map((item) => (
          <Paper
            key={item.title}
            component={RouterLink}
            to={item.href}
            sx={{
              flex: 1,
              p: 1.5,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              textDecoration: 'none',
              color: 'inherit',
              transition: 'all 0.2s',
              display: 'block',
              '&:hover': {
                borderColor: `${item.iconColor}66`,
                boxShadow: `0 4px 12px ${item.iconColor}26`,
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: item.gradient,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: `0 4px 10px ${item.iconColor}40`,
                }}
              >
                <Iconify icon={item.icon} width={20} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='subtitle2'
                  fontWeight={700}
                  sx={{ fontSize: 13.5, lineHeight: 1.3 }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontSize: 11.5, lineHeight: 1.3, display: 'block', mt: 0.25 }}
                >
                  {item.description}
                </Typography>
              </Box>

              <Button
                size='small'
                variant='text'
                endIcon={<Iconify icon='eva:arrow-ios-forward-fill' width={14} />}
                sx={{
                  color: item.iconColor,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'none',
                  flexShrink: 0,
                  display: { xs: 'none', sm: 'inline-flex' },
                  '&:hover': { bgcolor: `${item.iconColor}14` },
                }}
              >
                {item.cta}
              </Button>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Paper>
  )
}
